/*
Here's what I did to make this file
1. Copied import axios line from top of generated\api.ts
2. Copied FilesClient out of generated\api.ts
3. Copied throwException and isAxiosError from bottom of generated\api.ts
4. Renamed FilesClient to be FilesWithProgressClient
5. Imported everything else that was missing
6. Copied the couple of eslint/tslint "shut up" lines from the top of generated\api.ts
7. Added onDownloadProgress and onUploadProgress callbacks to whatever needs them

Here's what we'll need to do if we regenerate and the copy pasted code has changed
1. Copy the FilesClient again
2. Add the onDownloadProgress/onUploadProgress back into the signatures and options_ AxiosRequestConfig building
3. Import any new imports that are needed
 */

/* tslint:disable */
/* eslint-disable */


import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios';
import { ApiException, FileParameter, FileResponse, PostUploadFileResponseModel, ProblemDetails, ValidationProblemDetails } from './generated/api';


export class FilesWithProgressClient {
  protected instance: AxiosInstance;
  protected baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(baseUrl?: string, instance?: AxiosInstance) {
    this.instance = instance || axios.create();

    this.baseUrl = baseUrl ?? '';
  }

  /**
   * API09001 - Get File
   * @return OK
   */
  getFile(gid: string, cancelToken?: CancelToken, onDownloadProgress?: (progressEvent: ProgressEvent) => void): Promise<FileResponse> {
    let url_ = this.baseUrl + '/api/v1/Files/{gid}';
    if (gid === undefined || gid === null) throw new Error("The parameter 'gid' must be defined.");
    url_ = url_.replace('{gid}', encodeURIComponent('' + gid));
    url_ = url_.replace(/[?&]$/, '');

    const options_: AxiosRequestConfig = {
      responseType: 'blob',
      method: 'GET',
      url: url_,
      headers: {
        Accept: 'application/json',
      },
      cancelToken,
      onDownloadProgress,
    };

    return this.instance
      .request(options_)
      .catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse) => {
        return this.processGetFile(_response);
      });
  }

  protected processGetFile(response: AxiosResponse): Promise<FileResponse> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 401) {
      const _responseText = response.data;
      return throwException('Unauthorized', status, _responseText, _headers);
    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      const resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException('Forbidden', status, _responseText, _headers, result403);
    } else if (status === 422) {
      const _responseText = response.data;
      let result422: any = null;
      const resultData422 = _responseText;
      result422 = ValidationProblemDetails.fromJS(resultData422);
      return throwException('Unprocessable Content', status, _responseText, _headers, result422);
    } else if (status === 500) {
      const _responseText = response.data;
      let result500: any = null;
      const resultData500 = _responseText;
      result500 = ProblemDetails.fromJS(resultData500);
      return throwException('Internal Server Error', status, _responseText, _headers, result500);
    } else if (status === 200 || status === 206) {
      const contentDisposition = response.headers ? response.headers['content-disposition'] : undefined;
      let fileNameMatch = contentDisposition
        ? /filename\*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/g.exec(contentDisposition)
        : undefined;
      let fileName = fileNameMatch && fileNameMatch.length > 1 ? fileNameMatch[3] || fileNameMatch[2] : undefined;
      if (fileName) {
        fileName = decodeURIComponent(fileName);
      } else {
        fileNameMatch = contentDisposition ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition) : undefined;
        fileName = fileNameMatch && fileNameMatch.length > 1 ? fileNameMatch[1] : undefined;
      }
      return Promise.resolve({
        fileName: fileName,
        status: status,
        data: new Blob([response.data], { type: response.headers['content-type'] }),
        headers: _headers,
      });
    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      const resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException('Not Found', status, _responseText, _headers, result404);
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException('An unexpected server error occurred.', status, _responseText, _headers);
    }
    return Promise.resolve<FileResponse>(null as any);
  }

  /**
   * API09005 - POST Upload File
   * @param file (optional)
   * @return OK
   */
  postUploadFile(file?: FileParameter | undefined, cancelToken?: CancelToken, onUploadProgress?: (progressEvent: ProgressEvent) => void): Promise<PostUploadFileResponseModel> {
    let url_ = this.baseUrl + '/api/v1/Files/upload';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = new FormData();
    if (file === null || file === undefined) throw new Error("The parameter 'file' cannot be null.");
    else content_.append('file', file.data, file.fileName ? file.fileName : 'file');

    const options_: AxiosRequestConfig = {
      data: content_,
      method: 'POST',
      url: url_,
      headers: {
        Accept: 'application/json',
      },
      cancelToken,
      onUploadProgress,
    };

    return this.instance
      .request(options_)
      .catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse) => {
        return this.processPostUploadFile(_response);
      });
  }

  protected processPostUploadFile(response: AxiosResponse): Promise<PostUploadFileResponseModel> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 401) {
      const _responseText = response.data;
      return throwException('Unauthorized', status, _responseText, _headers);
    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      const resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException('Forbidden', status, _responseText, _headers, result403);
    } else if (status === 422) {
      const _responseText = response.data;
      let result422: any = null;
      const resultData422 = _responseText;
      result422 = ValidationProblemDetails.fromJS(resultData422);
      return throwException('Unprocessable Content', status, _responseText, _headers, result422);
    } else if (status === 500) {
      const _responseText = response.data;
      let result500: any = null;
      const resultData500 = _responseText;
      result500 = ProblemDetails.fromJS(resultData500);
      return throwException('Internal Server Error', status, _responseText, _headers, result500);
    } else if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      const resultData200 = _responseText;
      result200 = PostUploadFileResponseModel.fromJS(resultData200);
      return Promise.resolve<PostUploadFileResponseModel>(result200);
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException('An unexpected server error occurred.', status, _responseText, _headers);
    }
    return Promise.resolve<PostUploadFileResponseModel>(null as any);
  }
}

function throwException(
  message: string,
  status: number,
  response: string,
  headers: { [key: string]: any },
  result?: any,
): any {
  if (result !== null && result !== undefined) throw result;
  else throw new ApiException(message, status, response, headers, null);
}

function isAxiosError(obj: any): obj is AxiosError {
  return obj && obj.isAxiosError === true;
}
